/**
 * @fileoverview Expose a lead's durable workflow ledger for read-only inspection. `kc-internal-backend/services/intake/workflow/__init__.py` owns instances, commands, gates, effects, and timers; `kc-internal-frontend/src/components/Intake/intakeApi.js` retrieves their projection. Refresh affects inspection state rather than acknowledging commands or advancing the workflow.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { fetchIntakeLeadWorkflow } from '../intakeApi';
import { EmptyState } from '../IntakeDetailPrimitives';
import { formatDate, titleize } from '../intakeFormatters';
import AppIcon from '../../shared/AppIcon';



function getWorkflowRuntimeTone(status) {
  if (['failed', 'error'].includes(status)) return 'danger';
  if (['open', 'blocked', 'waiting', 'pending'].includes(status)) return 'warning';
  if (['running', 'processing'].includes(status)) return 'info';
  if (['completed', 'recorded', 'managed_by_workflow'].includes(status)) return 'success';
  return 'muted';
}



function getWorkflowRuntimeIcon(kind) {
  if (kind === 'signal') return 'pi pi-bolt';
  if (kind === 'command') return 'pi pi-cog';
  if (kind === 'timer') return 'pi pi-clock';
  if (kind === 'gate') return 'pi pi-user-edit';
  if (kind === 'effect') return 'pi pi-send';
  if (kind === 'step') return 'pi pi-list-check';
  return 'pi pi-circle';
}



function formatWorkflowPayload(payload) {
  try {
    return JSON.stringify(payload || {}, null, 2);
  } catch {
    return '{}';
  }
}



export function WorkflowRuntimeSection({ leadId }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const loaded = Boolean(payload || error);
  const timeline = payload?.timeline || [];
  const summary = payload?.summary || null;

  const loadRuntime = useCallback(

  () => {
    if (!leadId || loading || loaded) return;
    setLoading(true);
    setError('');
    fetchIntakeLeadWorkflow(leadId)
    .then((response) => {
      setPayload(response.data);
    })
    .catch((err) => {
      console.error('Failed to load intake workflow runtime:', err);
      setError('Internal runtime failed to load.');
    })
    .finally(() => {
      setLoading(false);
    });
  }, [leadId, loaded, loading]);

  const handleToggle =

  (event) => {
    if (event.currentTarget.open) loadRuntime();
  };

  useEffect(() => {
    setPayload(null);
    setError('');
    setLoading(false);
  }, [leadId]);

  return (
    <details className="intake-workflow-runtime" onToggle={handleToggle}>
      <summary>
        <div>
          <span className="intake-pipeline-eyebrow">Internal</span>
          <strong>Runtime detail</strong>
        </div>
        <AppIcon icon="pi pi-chevron-down" aria-hidden="true" />
      </summary>
      <div className="intake-workflow-runtime__body">
        {loading && <div className="intake-loading">Loading internal runtime...</div>}
        {error && <div className="intake-inline-alert" role="alert"><span>{error}</span></div>}
        {!loading && !error && payload &&
          <>
            <div className="intake-workflow-runtime__summary">
              <strong>{summary?.pipeline_stage?.label || summary?.label || 'Pipeline idle'}</strong>
              <span>{summary?.pipeline_stage?.detail || summary?.detail || 'No pipeline runtime activity recorded.'}</span>
              {summary?.next_action_at && <small>{formatDate(summary.next_action_at)}</small>}
            </div>
            {!timeline.length &&
              <EmptyState title={payload.enabled ? 'No internal runtime events yet' : 'Internal runtime disabled'} />}
            {timeline.length > 0 &&
              <ol className="intake-workflow-runtime__timeline">
                {timeline.map((item) => {
                  const tone = getWorkflowRuntimeTone(item.status);
                  return (
                    <li key={`${item.kind}:${item.id}`} className={`intake-workflow-runtime-item intake-workflow-runtime-item--${tone}`}>
                      <div className="intake-workflow-runtime-item__icon">
                        <AppIcon icon={getWorkflowRuntimeIcon(item.kind)} aria-hidden="true" />
                      </div>
                      <div className="intake-workflow-runtime-item__body">
                        <div className="intake-workflow-runtime-item__title">
                          <strong>{item.title || titleize(item.type)}</strong>
                          <span>{titleize(item.kind)}{item.status ? ` / ${titleize(item.status)}` : ''}</span>
                        </div>
                        {item.detail && <p>{item.detail}</p>}
                        <div className="intake-workflow-runtime-item__meta">
                          {item.occurred_at && <small>{formatDate(item.occurred_at)}</small>}
                          {item.type && <small>{item.type}</small>}
                        </div>
                        <details className="intake-workflow-runtime-payload">
                          <summary>Payload</summary>
                          <pre>{formatWorkflowPayload(item.payload)}</pre>
                        </details>
                      </div>
                    </li>
                  );
                })}
              </ol>}
          </>}
      </div>
    </details>
  );
}
